//@ts-nocheck
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { getRequest } from "../../utils/jha-fetch";
import { Apis } from "../../utils/api-constants";
import { getRoleData } from "../../utils/getRoleData";
import { decryptValue, stringComparison, getEnvApiUrl } from "../../utils/app-utils";
import "@banno/jha-wc/src/forms/jha-form-input-base/jha-form-input";
import "@banno/jha-wc/src/forms/jha-form-select/jha-form-select";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/forms/jha-form-row/jha-form-row";
import "@jack-henry/jh-ui/components/input/input.js";
import "@jack-henry/jh-ui/components/tooltip/tooltip.js";
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import Cookies from "js-cookie";
import { recordGeneralInfoAutoFill } from "../../utils/workflow-cancel-dirty";

@customElement("workflow-general-info")
export class WorkflowGeneralInfoView extends formElementMixin(LitElement) {
  constructor() {
    super();
  }
  static readonly styles = css`
    jha-form-row {
      align-items: baseline;
      column-gap: 20px;
    }

    #genInfoDiv {
      display: grid;
      grid-template-columns: auto auto auto;
      column-gap: 24px;
      padding-right: 15px;
      padding-left: 15px;
    }
    @media (prefers-color-scheme: dark) {
      h2 {
        color: #a3d4ff;
      }
    }
    @media (prefers-color-scheme: light) {
      h2 {
        color: #06185f;
      }
    }
    h2 {
      grid-column: 1/4;
      font-size: 24px;
      font-weight: 600;
    }
    .edit-note {
      font-size: 14px;
      padding-left: 15px;
      padding-right: 15px;
    }
  `;

  @property({ type: Array })
  transferType: any[] = [];
  @property({ type: Array })
  triggerFileList: any[] = [];
  @property({ type: Array })
  businessUnits = [];
  @property({ type: Array })
  dataFileList: string[] = [];
  @property({ type: String })
  triggerFileName = "";
  @property({ type: String })
  dataFileName = "";
  @property({ type: Object })
  workflowInfo = {};
  @property({ type: String })
  loggedUserType = decryptValue(Cookies.get("userType") || "");
  @property({ type: String })
  loggedUserBu = Cookies.get("businessUnit");
  @property({ type: Object })
  formData = {
    WorkflowName: "",
    TransferType: "",
    BusinessUnitParticipant: stringComparison(decryptValue(Cookies.get("userType") || ""), "BusinessAdmin")
      ? this.loggedUserBu?.toLowerCase()
      : "",
    sourceLocationPath: "",
    TriggerFilePattern: [] as any[],
    DataFilePattern: [] as any[],
    valid: false,
    isEdit: false,
    isSourceLocationValid: false,
    isWorkflowNameValid: false,
  };
  @property()
  warnMsg = "";
  @property({ type: Array })
  fileArr = [];
  @property()
  toUpdate = "";
  @property({ type: Array })
  dataArr = [];
  @property()
  pageTitle = "Add Workflow";
  @property({ type: Boolean })
  isReadOnlyUser = /readonly/i.test(decryptValue(Cookies.get("userRole") || ""));

  /**
   * Resolves once the lookups below — and the Business Admin BU auto-fill they gate — have landed.
   *
   * ACME-1050: the auto-fill mutates `formData`, which parents pass in by reference, so a Cancel
   * dirty baseline captured before this settles reads an untouched form as dirty. Parents must
   * await this first; see `afterGeneralInfoLoad` in `utils/workflow-cancel-dirty.ts`.
   * Never rejects — a failed lookup must not wedge the caller's baseline capture.
   */
  loadComplete: Promise<void> = Promise.resolve();
  /** True after loadLookups finishes (success or fail). Parents fold on this, not on a later settle. */
  lookupsDone = false;
  /** BU value this load wrote for Business Admin auto-fill; undefined if auto-fill did not run. */
  autoFilledBusinessUnitParticipant: string | undefined = undefined;

  async connectedCallback() {
    super.connectedCallback();
    document.title = document.title.concat(" >", this.pageTitle);
    this.loadComplete = this.loadLookups()
      .catch((err) => {
        const detail = err instanceof Error ? err.message : String(err);
        console.error("workflow-general-info lookups failed", detail);
      })
      .finally(() => {
        this.lookupsDone = true;
        recordGeneralInfoAutoFill(this.formData, this.autoFilledBusinessUnitParticipant);
      });
    await this.loadComplete;
  }

  private async loadLookups() {
    // getEnvApiUrl: localhost-only; .env.local never ships — no effect in prod, test, staging, or CI builds.
    const transferTypeData = await getRequest(getEnvApiUrl(Apis.WORKFLOW_TYPE));
    this.transferType = getRoleData(transferTypeData?.list ?? []);
    const businessUnitsAllRes = await getRequest(getEnvApiUrl(Apis.GET_ALL_BUSINESSUNITVALUES));
    const businessUnitsRes = businessUnitsAllRes?.businessUnitValues ?? [];
    this.businessUnits = businessUnitsRes.filter((bu) => bu.status === "active" ||
      (this.formData.isClone && bu.status === "active" && bu.name.toLowerCase() === this.formData.BusinessUnitParticipant?.toLowerCase()) ||
      (this.formData.isClone && bu.status === "active" && bu.value.toLowerCase() === this.loggedUserBu?.toLowerCase())
      || (this.formData.isEdit && bu.value.toLowerCase() === this.loggedUserBu?.toLowerCase())
      || (this.formData.isEdit && bu.value.toLowerCase() === this.formData.BusinessUnitParticipant?.toLowerCase()))
      .map((y: { value: any; name: any }) => {
        return { value: y.value, name: y.name };
      })
      .sort((a: { name: string }, b: { name: any }) => a.name.localeCompare(b.name));
    this.triggerFileList = this.formData.TriggerFilePattern;
    this.dataFileList = this.formData.DataFilePattern;
    this.isEditable = this.formData.isEdit;
    if (!this.formData.isEdit && !stringComparison(this.loggedUserType, "EnterpriseAdmin")) {
      const bu = this.businessUnits.find((bu) => bu.value.toLowerCase() === this.loggedUserBu?.toLowerCase());
      if (bu) {
        const filled = bu.value.toLowerCase();
        this.formData.BusinessUnitParticipant = filled;
        this.autoFilledBusinessUnitParticipant = filled;
      }
    }
  }

  onTriggerFileNameChange(e: Event): void {
    this.triggerFileName = (e.target as HTMLInputElement).value;
  }

  onTriggerFileAdd() {
    if (this.triggerFileName != "") {
      this.triggerFileList.push(this.triggerFileName);
      this.formData.TriggerFilePattern = this.triggerFileList;
      this.toUpdate = this.toUpdate == "" ? "update" : "";
    }
  }

  onDataFileNameChange(e: Event): void {
    this.dataFileName = (e.target as HTMLInputElement).value;
  }

  onDataFileAdd() {
    if (this.dataFileName != "") {
      this.dataFileList.push(this.dataFileName);
      this.formData.DataFilePattern = this.dataFileList;
      this.toUpdate = this.toUpdate == "" ? "update" : "";
    }
  }

  onEditTriggerName(i: string) {
    this.triggerFileName = i;
    const deleteIndex = this.triggerFileList.indexOf(i);
    this.triggerFileList.splice(deleteIndex, 1);
    this.toUpdate = this.toUpdate == "" ? "update" : "";
  }

  onDeleteTriggerName(i: string) {
    const deleteIndex = this.triggerFileList.indexOf(i);
    this.triggerFileList.splice(deleteIndex, 1);
    this.formData.TriggerFilePattern = this.triggerFileList;
    this.toUpdate = this.toUpdate == "" ? "update" : "";
  }

  onEditDataName(i: string) {
    this.dataFileName = i;
    const deleteIndex = this.dataFileList.indexOf(i);
    this.dataFileList.splice(deleteIndex, 1);
    this.toUpdate = this.toUpdate == "" ? "update" : "";
  }

  onDeleteDataName(i: string) {
    const deleteIndex = this.dataFileList.indexOf(i);
    this.dataFileList.splice(deleteIndex, 1);
    this.formData.DataFilePattern = this.dataFileList;
    this.toUpdate = this.toUpdate == "" ? "update" : "";
  }

  setWarning(e: { target: { value: any } }) {
    console.log(e.target.value);
  }

  onSLPchange(e: { target: any }) {
    const value = e?.target?.value ?? "";
    if (value) {
      const loaded = this.formData.loadedSourceLocationSuffix;
      if (
        this.formData.isEdit &&
        typeof loaded === "string" &&
        loaded &&
        value.toLowerCase() === loaded.toLowerCase()
      ) {
        const persistedField = this.shadowRoot?.querySelector(
          "jha-form-input[data-auto-id='sourceLocationPathInput']"
        ) as any;
        if (persistedField) {
          persistedField.showWarning = false;
        }
        this.formData.isSourceLocationValid = true;
        this.updateProperty("sourceLocationPath", value);
        this.syncFormValid();
        return;
      }
      const regex = /^\/([a-zA-Z0-9#_\-]+\/)*[a-zA-Z0-9#_\-]+\/$/;
      const strRegex = /^[a-zA-Z0-9]+$/;
      let inValid: boolean = false;
      let warningMessage = "";

      if (!value.startsWith("/")) {
        inValid = true;
        warningMessage =
          "Please enter a valid path. It must start with /. Example: /Folder/Path/";
      } else if (!value.endsWith("/")) {
        inValid = true;
        warningMessage =
          "Please enter a valid path. It must end with /. Example: /Folder/Path/";
      } else if (/\s/.test(value)) {
        inValid = true;
        warningMessage =
          "Please enter a valid input. Spaces are not allowed in the path.";
      } else if (!regex.test(value)) {
        inValid = true;
        warningMessage =
          "Please enter a valid path. Each folder name may contain only letters, numbers, #, underscores, and hyphens.";
      } else {
        const innerValues = value.split("/");
        innerValues.forEach((element: string) => {
          if (element !== "" && (!strRegex.test(element[0]) || !strRegex.test(element.slice(-1)))) {
            inValid = true;
            warningMessage =
              "Please enter a valid path. Each folder name must start and end with a letter or number.";
          }
        });
      }

      const invalidField = this.shadowRoot?.querySelector(
        "jha-form-input[data-auto-id='sourceLocationPathInput']"
      ) as any;
      if (inValid) {
        if (invalidField) {
          invalidField.warning = warningMessage;
          invalidField.showWarning = true;
        }
        this.formData.isSourceLocationValid = false;
        this.updateProperty("sourceLocationPath", value);
      } else {
        if (invalidField) {
          invalidField.showWarning = false;
        }
        this.formData.isSourceLocationValid = true;
        this.updateProperty("sourceLocationPath", value);
      }
      this.syncFormValid();
    } else {
      this.formData.isSourceLocationValid = false;
      this.updateProperty("sourceLocationPath", value);
      this.syncFormValid();
    }
  }

  onTransferTypeChange(e: { target: { value: string } }) {
    this.formData.TransferType = e.target.value;

    if (e.target.value == "pull") {
      this.formData.isSourceLocationValid = true;
      this.updateProperty("sourceLocationPath", "");
    } else if (!this.formData.isEdit) {
      this.formData.isSourceLocationValid = false;
      this.updateProperty("sourceLocationPath", "");
    }

    this.syncFormValid();
    this.requestUpdate();
  }

  private syncFormValid(): void {
    this.formData.valid =
      this.formValid && this.formData.isSourceLocationValid && this.formData.isWorkflowNameValid;
  }

  validateWorkflowName(name: string) {
    const workflowField = this.shadowRoot.querySelector("jha-form-input[data-auto-id='workflowNameInput']");
    const regex = /^[A-Za-z0-9._\-]+$/;
    if (!name) {
      this.workflowNameError = "Workflow Name is required";
      workflowField.showWarning = true;
      this.formData.isWorkflowNameValid = false;
    } else if (!regex.test(name)) {
      this.workflowNameError = "Workflow Name is invalid";
      workflowField.showWarning = true;
      this.formData.isWorkflowNameValid = false;
    } else {
      this.workflowNameError = "";
      workflowField.showWarning = false;
      this.formData.isWorkflowNameValid = true;
    }
    if (!this.formData.isEdit && stringComparison(this.formData.TransferType, "pull")) {
      this.formData.isSourceLocationValid = this.formData.isWorkflowNameValid;
    }
    this.updateProperty("WorkflowName", name);
    this.syncFormValid();
    this.requestUpdate();
  }

  inputValueChange(e: { target: { dataset: { id: string }; value: string } }) {
    super.inputValueChange(e);
    this.syncFormValid();
  }

  render() {
    this.dispatchEvent(
      new CustomEvent("onWorkflowNameChange", {
        detail: this.formData,
        bubbles: true,
        composed: true,
      })
    );

    return html`
      <form @focusout=${() => this.syncFormValid()}>
        ${this.formData.isEdit
        ? html`<p class="edit-note">
              Note: Workflow Name, Business Unit and Source Location Path fields will be disabled for workflows associated with a
              scheduler.
            </p>`
        : ``}
        <div id="genInfoDiv">
          <h2>General Information</h2>
          <jha-tooltip bottom text="Use only letters, numbers, dots, dashes or underscores. Max length 64 characters.">
            <jha-form-input
              label="Workflow Name"
              placeholder="Enter Workflow Name"
              required
              .warning=${this.workflowNameError}
              type="text"
              outline
              ?disabled=${(this.formData.isEdit && stringComparison(this.formData.TransferType, "pull")) || this.isReadOnlyUser}
              data-id="WorkflowName"
              data-auto-id="workflowNameInput"
              .value=${this.formData.WorkflowName}
              @change=${(e: Event) => this.validateWorkflowName((e.target as HTMLInputElement).value)}
              maxlength="64"
              characterLimit="64"></jha-form-input>
          </jha-tooltip>
          <jha-tooltip bottom text="Pull is downloaded to source location. Push is uploaded from source location.">
            <jha-form-select
              outline
              .options=${this.transferType ?? []}
              label="Transfer Type"
              required
              warning="Transfer Type is required"
              lableKey="name"
              valueKey="value"
              isValid
              data-id="TransferType"
              data-auto-id="transferTypeSelect"
              ?disabled=${this.formData.isEdit || this.isReadOnlyUser || this.formData.isClone}
              .value=${this.formData.TransferType}
              @change=${this.onTransferTypeChange}
              emptyOptionLabel="Select Transfer Type">
            </jha-form-select>
          </jha-tooltip>
          <jha-tooltip bottom text="Select a business unit for this workflow.">
            <jha-form-select
              outline
              label="Business Unit"
              .options=${this.businessUnits ?? []}
              lableKey="name"
              valueKey="value"
              data-id="BusinessUnitParticipant"
              data-auto-id="businessUnitSelect"
              ?disabled=${this.formData.isEdit ||
      this.isReadOnlyUser ||
      stringComparison(this.loggedUserType, "BusinessAdmin") ||
      (this.formData.isClone && !stringComparison(this.loggedUserType, "EnterpriseAdmin")) ||
      this.formData.isClone === false}
              value=${ifDefined(this.formData.BusinessUnitParticipant)}
              @change=${this.inputValueChange}
              required
              warning="Business Unit is Required"
              emptyOptionLabel="Select Business Unit">
            </jha-form-select>
          </jha-tooltip>
          ${!stringComparison(this.formData.TransferType, "pull")
        ? html`<h2>Source Location</h2>
                <jha-tooltip
                  bottom
                  text="Include forward slash ('/') at beginning of path and forward slash ('/') at end of path. Periods ('.') are not allowed. Max length of 256 characters.">
                  <div>
                    <jha-form-input
                      label="Source Location Path"
                      required
                      placeholder="Eg: /Path/"
                      warning="Source Location Path is required"
                      type="textarea"
                      outline
                      ?disabled=${(this.formData.isEdit && stringComparison(this.formData.TransferType, "pull")) ||
          this.isReadOnlyUser}
                      data-id="sourceLocationPath"
                      data-auto-id="sourceLocationPathInput"
                      .value=${this.formData.sourceLocationPath}
                      @change=${this.onSLPchange}
                      maxlength="256"
                      characterLimit="256">
                    </jha-form-input>
                  </div>
                </jha-tooltip>`
        : undefined}
        </div>
      </form>
    `;
  }
}
declare global {
  interface HTMLElementTagNameMap {
    "workflow-general-info": WorkflowGeneralInfoView;
  }
}
